// Penghubung ke Cloudflare Worker (Tahap 2).
// Alamat API diisi di frontend/.env  ->  REACT_APP_ALBUM_API=https://....workers.dev
import { applyFilter } from "@/lib/photoFilter";

const BASE = (process.env.REACT_APP_ALBUM_API || "").replace(/\/$/, "");

async function call(path, { method = "GET", headers = {}, body } = {}) {
  if (!BASE) throw new Error("Alamat API belum diatur (REACT_APP_ALBUM_API).");
  let r;
  try {
    r = await fetch(BASE + path, { method, headers, body });
  } catch {
    throw new Error("Tidak bisa terhubung ke server. Periksa internetmu.");
  }
  let data = null;
  try {
    data = await r.json();
  } catch {}
  if (!r.ok) {
    const e = new Error(data?.error || "Permintaan gagal");
    e.status = r.status;
    throw e;
  }
  return data;
}

/* ---------- ID perangkat tamu ---------- */
// Kunci baru ("dirga:device2") supaya ID rusak dari versi lama diabaikan sama sekali.
const DEVICE_KEY = "dirga:device2";
const DEVICE_OK = /^[a-z0-9]{6,32}$/; // aturan yang sama dengan server
const makeDevice = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(12)))
    .map((b) => "abcdefghjkmnpqrstuvwxyz23456789"[b % 31])
    .join("");
export const deviceId = () => {
  try {
    // buang tanda kutip/spasi/karakter lain kalau ada, lalu cek formatnya
    let d = String(localStorage.getItem(DEVICE_KEY) || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!DEVICE_OK.test(d)) d = makeDevice();
    localStorage.setItem(DEVICE_KEY, d);
    return d;
  } catch {
    return "tamu" + Math.random().toString(36).slice(2, 12).replace(/[^a-z0-9]/g, "x");
  }
};

/* ---------- Nama tamu (diingat di HP supaya tidak mengetik ulang) ---------- */
export const getGuestName = () => {
  try {
    return localStorage.getItem("dirga:guestname") || "";
  } catch {
    return "";
  }
};
export const setGuestName = (n) => {
  try {
    localStorage.setItem("dirga:guestname", n);
  } catch {}
};

/* ---------- Kompres foto di browser ---------- */
const loadImg = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
const draw = (img, max) => {
  const r = Math.min(1, max / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * r);
  c.height = Math.round(img.height * r);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c;
};
export const compressImage = async (file, max = 1200, q = 0.72) =>
  draw(await loadImg(file), max).toDataURL("image/jpeg", q);
// filter = id nuansa foto (lihat config/album.js). "asli" = tanpa efek.
export const compressToBlob = async (file, max = 1600, q = 0.8, filter = "asli") => {
  const c = applyFilter(draw(await loadImg(file), max), filter);
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("Foto tidak bisa diproses"))), "image/jpeg", q));
};

/* Dua ukuran dari satu foto, dua-duanya sudah bernuansa:
   - gallery: kecil dan ringan untuk galeri (hemat permintaan dan kuota tamu)
   - zip: lebih besar dan tajam, ini yang masuk ke ZIP pengantin
   Nuansa dipasang SEKALI pada ukuran besar, lalu diperkecil untuk galeri. */
export const GALLERY_MAX = 1280;
export const GALLERY_Q = 0.75;
export const ZIP_MAX = 2048; // 2048 px cukup tajam untuk cetak sekitar 13 x 18 cm
export const ZIP_Q = 0.82;
const toBlob = (c, q) =>
  new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("Foto tidak bisa diproses"))), "image/jpeg", q));
export const compressPair = async (file, filter = "asli") => {
  const img = await loadImg(file);
  const big = applyFilter(draw(img, ZIP_MAX), filter);
  const small = draw(big, GALLERY_MAX);
  return { gallery: await toBlob(small, GALLERY_Q), zip: await toBlob(big, ZIP_Q) };
};

/* Foto asli yang boleh dikirim apa adanya untuk unduhan pengantin */
export const ORIGINAL_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const ORIGINAL_MAX = 25 * 1024 * 1024;
export const canKeepOriginal = (file) => ORIGINAL_TYPES.includes(file.type) && file.size <= ORIGINAL_MAX;
const EXT = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };
export const extOf = (type) => EXT[type] || "jpg";

/* ---------- Pemilik acara: kirim permintaan ---------- */
export const createRequest = async (f) => {
  const fd = new FormData();
  ["eventType", "eventName", "couple", "textColor", "overlay", "filter", "startDate", "endDate"].forEach((k) => fd.append(k, f[k] ?? ""));
  fd.append("allowGuestView", String(!!f.allowGuestView));
  const blob = await (await fetch(f.cover)).blob();
  fd.append("cover", blob, "cover.jpg");
  return call("/api/requests", { method: "POST", body: fd });
};

/* ---------- Admin ---------- */
const A = (pw) => ({ "x-admin-key": pw });
export const adminList = (pw) => call("/api/admin/requests", { headers: A(pw) });
export const adminActivate = (pw, rid) => call(`/api/admin/requests/${rid}/activate`, { method: "POST", headers: A(pw) });
export const adminDelete = (pw, rid) => call(`/api/admin/requests/${rid}`, { method: "DELETE", headers: A(pw) });

/* ---------- Tamu ---------- */
export const getAlbum = (id) => call(`/api/albums/${id}`);
export const getQuota = (id) => call(`/api/albums/${id}/quota?device=${deviceId()}`);
export const getPhotos = (id) => call(`/api/albums/${id}/photos?device=${deviceId()}`);
// blobs = versi tampilan (kecil). originals = foto asli, urutan sama dengan blobs (boleh kosong).
export const uploadPhotos = (id, blobs, name = "", originals = []) => {
  const fd = new FormData();
  fd.append("device", deviceId());
  fd.append("name", name);
  blobs.forEach((b, i) => fd.append("files", b, `foto${i}.jpg`));
  originals.forEach((b, i) => fd.append("originals", b, `asli${i}.${extOf(b.type)}`));
  return call(`/api/albums/${id}/photos`, { method: "POST", body: fd });
};
// Suka / batal suka. pin hanya dikirim oleh halaman pengantin.
export const likePhoto = (id, pid, on, pin) =>
  call(`/api/albums/${id}/like`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(pin ? { "x-pin": pin } : {}) },
    body: JSON.stringify({ pid, device: deviceId(), on }),
  });

/* ---------- Pemilik acara (PIN) ---------- */
export const manage = (id, pin) => call(`/api/albums/${id}/manage?device=${deviceId()}`, { headers: { "x-pin": pin } });
// Ambil satu foto dalam ukuran asli. isOriginal=false berarti foto lama (hanya ada versi tampilan).
export const fetchOriginal = async (id, pin, pid) => {
  if (!BASE) throw new Error("Alamat API belum diatur (REACT_APP_ALBUM_API).");
  const r = await fetch(`${BASE}/api/albums/${id}/original?pid=${pid}`, { headers: { "x-pin": pin } });
  if (!r.ok) throw new Error("Foto gagal diambil");
  return { blob: await r.blob(), isOriginal: r.headers.get("x-original") === "1" };
};